import { Channel } from 'channelx';
import { collect, Collection } from 'collect.js';
import { NavigationFailure, RouteRecordName, RouteRecordRaw } from 'vue-router';
import { WorkflowCompiler } from 'workflow-compiler-provider';
import { app } from '@/main';
import { Channels } from '@/app.manager/events-manager/Channels';
import { AccessEvent } from '@/app.manager/events-manager/events/AccessEvent';
import { NavigationProvider } from '@/modules/navigation/providers/navigationProvider';
import { NodeProvider, TLocatorOptions, traverse } from '@/modules/workflow';
import { RouteModel } from '@/modules/navigation/models/route.model';
import router from '@/router';
import { navbarDebugMessage } from '@/modules/navigation/providers/utils';
import { authService } from '@/shared/services';
import sendAnalyticsEvent from '@/shared/analytics/sendAnalyticsEvent';
import { EventTypes } from '@/shared/analytics/types';
import { getFullAnalyticsRoutePath } from '@/shared/analytics/helpers';

// @ts-ignore
export class RouteProvider extends NodeProvider {
  public static instance: any;

  public activeRoute?: RouteModel = undefined;

  public collection: Collection<RouteModel> = collect();

  declare protected lastActive?: RouteModel;

  navigationProvider?: NavigationProvider;

  /**
   * RouteProvider singleton constructor
   */
  constructor() {
    super();

    if (RouteProvider.instance) {
      // eslint-disable-next-line no-constructor-return
      return RouteProvider.getInstance();
    }

    RouteProvider.instance = this;

    this.navigationProvider = new NavigationProvider();

    // @ts-ignore
    router.afterEach(this.onAfterEach.bind(this));
  }

  /**
   * Workflow compiler accessor
   *
   * @protected
   */
  protected get workflowCompiler(): WorkflowCompiler {
    return app.config.globalProperties.$workflowManager.compiler;
  }

  /**
   * Singleton accessor, returns an instance of the RouteProvider
   */
  public static getInstance() {
    if (this.instance) {
      return this.instance;
    }

    return new this();
  }

  /**
   * Sets a collection of routes for further handling
   *
   * @param nodes
   */
  public setRoutes(nodes: any[]) {
    this.collection = this.collect(nodes);

    return this;
  }

  setActiveRoute(route: RouteModel) {
    this.activeRoute = route;
  }

  /**
   * Performs an advancing to the next available route
   */
  public advance(locatorOptions?: TLocatorOptions): Promise<NavigationFailure | void | undefined> {
    return router.replace({ name: this.getNextRoute(locatorOptions).routeName });
  }

  /**
   * Performs an advancing to the next available route
   */
  public goBack(): Promise<NavigationFailure | void | undefined> {
    return router.replace({ name: this.getPreviousRoute()?.routeName });
  }

  /**
   * Routes the navigation when app is loaded to the initial page
   */
  public route(isInit = false) {
    // Retrieve a page we are on
    const navigatedPage = router.currentRoute.value.name || router.currentRoute.value.path.split('/').pop();

    if (!navigatedPage && router.currentRoute.value.path !== '/') {
      navbarDebugMessage(`The path ${router.currentRoute.value.path} should have a name!`);

      return;
    }

    // If it's a part of the flow, do nothing, as we already in the flow.
    try {
      this.resolveRoute(navigatedPage);

      // TODO: DGC-27954 we fail here, because it pre-resolved as 404.
      //   one option is to wait for proper initial load on the page container @ AppDrawer
      //   other options is to check for 404 matching/initial setup, and do
      //   router.replace(router.currentRoute.value.fullPath)
      //   to refresh the route matcher
      // May also provide a flag to indicate the call is initial (loggedIn() sourced)
      if (isInit) {
        router.replace(router.currentRoute.value.fullPath);
      }

      return;
    } catch (e: any) {
      navbarDebugMessage(`Performing an initial routing. Reason: ${e.message}`, 'info');
    }

    // Otherwise, we need to redirect to the page from the state (if any)
    // Try to get a new page from the state
    const statePage = this.workflowCompiler?.getState?.page?.name;

    try {
      const statePageResolved = this.resolveRoute(statePage);

      // If found, navigate to it in the next tick as latest as possible (needs logic improvement)
      router.replace({ name: statePageResolved.routeName });

      return;
    } catch (e: any) {
      navbarDebugMessage(`Routing to the first page. Reason: ${e.message}`, 'info');
    }

    // Otherwise, we need to redirect to a default page (first page)
    const defaultPage = this.collection.first();

    router.replace({ name: defaultPage.routeName });
  }

  public resolveRoute(name?: RouteRecordName) {
    const resolved = traverse(r => r.routeName === name, this.collection);

    if (!resolved) {
      throw new Error(`Unable to resolve route ${String(name)}`);
    }

    return resolved;
  }

  protected collect(nodes: RouteModel[], parent?: RouteModel) {
    const preMapped = nodes.map((node: RouteModel) => {
      const routeModel = new RouteModel(node, this);

      routeModel.parent = parent;

      if (parent) {
        routeModel.isChild = true;
      }

      if ((node.children || []).length) {
        routeModel.collection = this.collect(node.children?.filter(n => n.type === 'Route') || [], routeModel);
      }

      return routeModel;
    });

    return collect(preMapped);
  }

  protected getNextRoute(locatorOptions?: TLocatorOptions) {
    const { activeRoute = this.collection.first() } = this;

    let activeRouteReached = false;

    let nextRoute!: RouteModel;

    this.getFlatRoutes().each((route: RouteModel) => {
      if (!activeRouteReached) {
        if (route === activeRoute) {
          activeRouteReached = true;
        }

        return true;
      }

      if (route.isBypassEnabled) {
        return true;
      }

      if (route.isChild && locatorOptions?.getNextParent) {
        return true;
      }

      nextRoute = route;

      return false;
    });

    return nextRoute;
  }

  getFlatRoutes() {
    const flat: RouteModel[] = [];

    // @ts-ignore
    this.collection.each((item: any) => {
      flat.push(item);

      if (item.collection.isNotEmpty()) {
        // @ts-ignore
        item.collection.each((subRoute: RouteModel) => flat.push(subRoute));
      }
    });

    return collect(flat);
  }

  protected getPreviousRoute(): RouteModel | undefined {
    if (!this) { return undefined; }

    const { activeRoute = this.collection.first() } = this;

    let previousRoute!: RouteModel;

    this.getFlatRoutes().each((route: RouteModel) => {
      if (route === activeRoute) {
        return false;
      }

      if (route.isBypassEnabled) {
        return true;
      }

      previousRoute = route;

      return true;
    });

    return previousRoute;
  }

  protected getPrevRoutes(): Collection<RouteModel> {
    const { activeRoute = this.collection.first() } = this;

    const prevRoutes: RouteModel[] = [];

    this.collection.each((route: RouteModel) => {
      if (route === activeRoute) {
        return false;
      }

      prevRoutes.push(route);

      return true;
    });

    return collect(prevRoutes);
  }

  // TODO: DGC-27954 done restore @Publisher
  // @Publisher('eventsChannel')
  onRouteChange() {
    Channel.use(Channels.eventsChannel).publish({
      eventName: 'navbarClicked',
      payload: () => {
        Channel.use(Channels.eventsChannel).publish({ eventName: AccessEvent.NEXT_CLICKED_COMPLETE });
      },
    });

    if (authService.isLoggedIn()) {
      sendAnalyticsEvent(EventTypes.PAGE_VIEW, {
        customEventData: {
          page_name: getFullAnalyticsRoutePath(),
          opened_menu: false,
        },
      });
    }
  }

  protected onAfterEach(to: RouteRecordRaw, from: RouteRecordRaw) {
    // "name" can be null on a first load when routes are not populated.
    const SPECIAL_ROUTES = ['TakeControl'];

    if (!to.name || SPECIAL_ROUTES.includes(to.name.toString())) { return; }

    try {
      const route = this.resolveRoute(to.name);

      this.updatePage(route);
      // Timeout is put for smooth UI transitions
      window.setTimeout(() => {
        this.onRouteChange();
        this.updateStatistics(route);
        this.updateNavbar(route);
        this.updatePage(route);
        this.updateProgress(route);

        Channel.use(Channels.navigationChannel).publish({ eventName: 'routeChangeComplete' });
      }, 100);
    } catch (e) {
      // TODO: DGC-27954 extra case; load order - loads BEFORE Register initialization
      if (to.name === 'register') {
        console.error('migration: new routes are not ready! Why?');

        return;
      }
      if (to.name !== 'login') {
        throw e;
      }
    }
  }

  protected updateProgress(route: RouteModel) {
    if (!this.workflowCompiler) {
      navbarDebugMessage('Unable to update a progress bar. Reason: missing compiler');

      return;
    }

    // Get the progress pages from the state, if any
    const stateProgressPageName: string | undefined = this.workflowCompiler?.getState?.progressPage?.name;

    const isNotGuarded = typeof route.guarded !== 'undefined' && !route.guarded;

    // If the current route comes before the progress page from the state, or if not guarded, don't update and skip
    if (isNotGuarded || (stateProgressPageName && route.isBeforeOrSame(stateProgressPageName))) { return; }

    // Otherwise, if no progress page in the state or the current route comes after, update the state
    this.workflowCompiler.setProgressPage({
      name: route.routeName,
      fullPath: route.fullPath,
    });
  }

  protected updatePage(route: RouteModel) {
    this.workflowCompiler.getState.location = '';

    this.workflowCompiler.setPage({
      name: route.routeName,
      fullPath: route.fullPath,
    });
  }

  protected updateNavbar(route: RouteModel) {
    return route.navigate();
  }

  protected updateStatistics(route: RouteModel) {
    if (!app.config.globalProperties.$igcStatistics) {
      navbarDebugMessage(`Unable to send stats for route ${route.routeName}. Reason: missing statistics service`, 'warn');

      return;
    }

    app.config.globalProperties.$igcStatistics.addPageTimeEvent(route.fullPath);
  }
}
